import clsx from "clsx";
import { useState, type ChangeEvent, type ReactElement } from "react";
import React from "react";
import type { JSX } from "react/jsx-runtime";

export type Suggestion = {
  text: string;
  selectedId: number;
  objectId: string;
  originalQuery?: string;
  categoryName?: string;
};

type Props = {
  onInputChange: (value: string) => void;
  onSelect: (suggestion?: Suggestion) => void;
  inputValue: string;
  placeholder: string;
  suggestions: Suggestion[];
  inputName: string;
  listId: string;
  dataTestId?: string;
};

function SearchCombobox({
  onInputChange,
  inputValue,
  placeholder,
  suggestions,
  inputName,
  onSelect,
  listId,
  dataTestId,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    setIsOpen(true);
    onInputChange(event.currentTarget.value);
  }

  function handleArrowDown(): void {
    setActiveIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
  }

  function handleArrowUp(): void {
    setActiveIndex(
      (prevIndex) => (prevIndex + suggestions.length - 1) % suggestions.length,
    );
  }

  function handleEnter(): void {
    onSelect(activeIndex >= 0 ? suggestions[activeIndex] : undefined);
  }

  function handleEscape(): void {
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(
    e: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>,
  ): void {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        handleArrowDown();
        break;
      case "ArrowUp":
        e.preventDefault();
        handleArrowUp();
        break;
      case "Enter":
        e.preventDefault();
        handleEnter();
        break;
      case "Escape":
        handleEscape();
        break;
      default:
        break;
    }
  }

  function handleSuggestionSelect(suggestion: Suggestion): void {
    setIsOpen(false);
    onSelect(suggestion);
  }

  function createSuggestionLabel(suggestion: Suggestion): ReactElement {
    return (
      <>
        {suggestion.originalQuery && <b>{suggestion.originalQuery}</b>}
        {suggestion.text}
        {suggestion.categoryName && ` in ${suggestion.categoryName}`}
      </>
    );
  }

  return (
    <div className="relative size-full">
      <input
        name={inputName}
        className="size-full max-h-xxxlarge min-h-xlarge text-ellipsis px-xxsmall py-xsmall text-onSurface placeholder:text-onSurface focus-visible:outline-0 focus-visible:ring-0"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        role="combobox"
        aria-controls={isOpen ? listId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-activedescendant={
          activeIndex >= 0 ? `option-${activeIndex}` : undefined
        }
        onFocus={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleEscape}
        autoComplete="off"
        data-testid={dataTestId}
      />
      {isOpen && (
        <ul
          id={listId}
          className="absolute z-dropdown mt-small w-max rounded-medium bg-surface py-xxsmall shadow-elevation"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => {
            return (
              <li
                id={`option-${index}`}
                key={suggestion.objectId + index}
                className={clsx(
                  "cursor-pointer p-xxsmall pl-large pr-small text-onSurface hover:bg-surfaceSubdued",
                  activeIndex === index && "bg-surfaceSubdued",
                )}
                onMouseDown={(): void => handleSuggestionSelect(suggestion)}
                role="option"
                aria-selected={activeIndex === index}
              >
                {createSuggestionLabel(suggestion)}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default SearchCombobox;
